import { useEffect, useMemo, useRef, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import {
  type EnrollmentHYCSPersonalInfoGetInput,
  useGetEnrollmentHYCSPersonalInfoData,
  useSubmitEnrollmentHYCSPersonalInfoData,
} from '@egw/api'
import { Input, Button, Alert, AlertType, GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { sendEvent } from '@egw/events'
import { useLocation } from 'react-router'
import { passkeyService } from '@egw/auth'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { dobIsValid, getNameValidationRule } from '../../../utils/form-validation'
import { getTestId, HtmlTestTypes } from '../../../utils/testing/get-test-id'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - HYCS - PersonalInfo')

interface FormData {
  firstName: string
  lastName: string
  dateOfBirth: string
}

export const PersonalInfoPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const token = query.get('token') || ''
  const isMobile = passkeyService.getIsMobile()

  const hasShownIntent = useRef(false)

  const dataLoadPayload: EnrollmentHYCSPersonalInfoGetInput = useMemo(() => {
    if (isMobile) {
      return {
        phoneMagicLinkVerificationToken: token,
      }
    }
    return {}
  }, [isMobile])

  const { data, isLoading, error } = useGetEnrollmentHYCSPersonalInfoData(dataLoadPayload)
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentHYCSPersonalInfoData()

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isDirty, isValid },
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
    },
    mode: 'onBlur',
  })

  // On intent
  useEffect(() => {
    if (isDirty && !hasShownIntent.current) {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_PERSONALINFO_INTENT)
      hasShownIntent.current = true // Prevent further executions
    }
  }, [isDirty])

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_PERSONALINFO_SUBMIT)
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_FULLNAME_SUBMIT)
      try {
        const [month, day, year] = formData.dateOfBirth.split('/')

        const response = await mutate({
          firstName: formData.firstName,
          lastName: formData.lastName,
          dateOfBirth: `${year}-${month}-${day}`,
        })

        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          // Disabling those - no risk check here anymore
          // sendEvent(EDataLayerEventWealth.BANKHYCHYS_RISKCHECK_VERIFIED)
          // sendEvent(EDataLayerEventWealth.EGM_SUBMISSION_PERSONAL_INFO_ACCEPTED, {
          //   orderId: response.enrollmentData.applicationId,
          // })
          navigate(response.path)
        } else {
          // If no path from BE, redirect to error
          if (!response?.path) {
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  return (
    <EnrollmentHYCSLayout title={t('hycs.personal_info.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && data ? (
          <form
            onSubmit={(e) => void onSubmit(e)}
            className="w-full flex-1 flex flex-col justify-start lg:justify-center"
          >
            {/* Alert */}
            <Alert
              alertType={AlertType.Success}
              description={t('hycs.personal_info.alert')}
              className="mb-md"
              testId="success-alert"
            />
            <PageHeader title={t('hycs.personal_info.title')} />
            <span className="mb-sm typo-primary-md-semibold text-text-header">
              {t('hycs.personal_info.names_label')}
            </span>
            <span className="mb-sm typo-primary-sm text-text-meta">{t('hycs.personal_info.names_description')}</span>
            {/* First Name */}
            <Controller
              name="firstName"
              control={control}
              rules={{
                required: t('hycs.personal_info.first_name_error'),
                ...getNameValidationRule(t('hycs.personal_info.first_name_error')),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('hycs.personal_info.first_name_label')}
                  required
                  error={formErrors.firstName?.message}
                  type="text"
                  className="mb-sm"
                  testId={getTestId('firstName', HtmlTestTypes.input)}
                />
              )}
            />
            {/* Last Name */}
            <Controller
              name="lastName"
              control={control}
              rules={{
                required: t('hycs.personal_info.last_name_error'),
                ...getNameValidationRule(t('hycs.personal_info.last_name_error')),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('hycs.personal_info.last_name_label')}
                  required
                  error={formErrors.lastName?.message}
                  type="text"
                  className="mb-lg"
                  testId={getTestId('lastName', HtmlTestTypes.input)}
                />
              )}
            />

            <span className="mb-sm typo-primary-md-semibold text-text-header">
              {t('hycs.personal_info.dob_section_label')}
            </span>
            <span className="mb-sm typo-primary-sm text-text-meta">
              {t('hycs.personal_info.dob_section_description')}
            </span>

            {/* Date of Birth */}
            <Controller
              name="dateOfBirth"
              control={control}
              rules={{
                required: t('hycs.personal_info.dob_error'),
                validate: (e) => dobIsValid(e) || t('hycs.personal_info.dob_error'),
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <Input
                  {...field}
                  value={value}
                  onChange={(e) => {
                    const input = e.target.value.replace(/\D/g, '')
                    let formatted = ''

                    if (input.length > 0) {
                      formatted = input.slice(0, 2)
                      if (input.length > 2) {
                        formatted += `/${input.slice(2, 4)}`
                        if (input.length > 4) {
                          formatted += `/${input.slice(4, 8)}`
                        }
                      }
                    }

                    if (formatted.length <= 10) {
                      onChange(formatted)
                    }
                  }}
                  label={t('hycs.personal_info.dob_label')}
                  required
                  error={formErrors.dateOfBirth?.message}
                  type="text"
                  className="mb-sm"
                  testId={getTestId('dateOfBirth', HtmlTestTypes.input)}
                />
              )}
            />
            <span className="mb-sm typo-primary-xs text-text-meta">{t('hycs.personal_info.dob_disclosure')}</span>

            <div className="flex-1 md:hidden" />

            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </form>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
